import * as React from "react";
import { useRef, useState } from "react";
import { join } from "../../utils/classNames";
import { useDismiss } from "../../hooks/useDismiss";
// Star and Pencil are imported from icons when the Favourite/Rename items are re-enabled below.
import { Trash } from "../icons";
import styles from "./SessionMenu.module.css";

/** Estimated menu height (px) used to decide whether it fits below the trigger before flipping up. */
const MENU_EST_HEIGHT = 140;

interface Props {
	favourite?: boolean;
	onToggleFavourite: () => void;
	/** Invoked when the user picks "Rename" — the caller shows its own rename UI. */
	onRename: () => void;
	onDelete: () => void;
	/** Trigger button content, class and label — lets the sidebar kebab and header title reuse this. */
	triggerContent: React.ReactNode;
	triggerClassName?: string;
	triggerAriaLabel: string;
	/** Menu edge alignment relative to the trigger. */
	align?: "left" | "right";
	/** Preferred vertical side. `"auto"` flips up when the trigger is near the viewport bottom. */
	side?: "below" | "above" | "auto";
	/** Notified when the menu opens/closes (e.g. to keep a hover-revealed trigger visible). */
	onOpenChange?: (open: boolean) => void;
}

export const SessionMenu: React.FC<Props> = ({
	favourite,
	onToggleFavourite,
	onRename,
	onDelete,
	triggerContent,
	triggerClassName,
	triggerAriaLabel,
	align = "right",
	side = "below",
	onOpenChange,
}) => {
	const [open, setOpenState] = useState(false);
	// When `side` is "auto", this holds the measured direction chosen at open time.
	const [flipUp, setFlipUp] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const setOpen = (next: boolean | ((o: boolean) => boolean)) => {
		setOpenState((prev) => {
			const value = typeof next === "function" ? next(prev) : next;
			if (value !== prev) onOpenChange?.(value);
			return value;
		});
	};

	useDismiss(open, rootRef, () => setOpen(false));

	const toggle = () => {
		// Decide the drop direction before opening: if the trigger sits within a menu's height
		// of the viewport bottom (e.g. a session row near the end of a scrolled list), flip up.
		if (!open && side === "auto" && triggerRef.current) {
			const rect = triggerRef.current.getBoundingClientRect();
			setFlipUp(window.innerHeight - rect.bottom < MENU_EST_HEIGHT);
		}
		setOpen((o) => !o);
	};

	const above = side === "above" || (side === "auto" && flipUp);

	const pick = (fn: () => void) => {
		setOpen(false);
		fn();
	};

	return (
		<div className={styles.root} ref={rootRef}>
			<button
				ref={triggerRef}
				type="button"
				className={triggerClassName}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-label={triggerAriaLabel}
				onClick={(e) => {
					e.stopPropagation();
					toggle();
				}}
			>
				{triggerContent}
			</button>
			{open && (
				<div
					className={join(styles.menu, align === "right" && styles.alignRight, above && styles.above)}
					role="menu"
					onClick={(e) => e.stopPropagation()}
				>
					{/* Favourite & Rename hidden — bring back when hooked to API. */}
					{/*
					<button type="button" role="menuitem" className={styles.item} onClick={() => pick(onToggleFavourite)}>
						<Star filled={favourite} />
						<span>{favourite ? "Unfavourite" : "Favourite"}</span>
					</button>
					<button type="button" role="menuitem" className={styles.item} onClick={() => pick(onRename)}>
						<Pencil />
						<span>Rename</span>
					</button>
					*/}
					<button
						type="button"
						role="menuitem"
						className={join(styles.item, styles.itemDanger)}
						onClick={() => pick(onDelete)}
					>
						<Trash />
						<span>Delete</span>
					</button>
				</div>
			)}
		</div>
	);
};
